/* eslint-disable consistent-return */
import { matchSorter } from "match-sorter";
import { useState, useMemo, useEffect } from "react";
import { AutoCompleteOption, useFilterOptionsProps } from "./types";

const useFilterOptions = ({
  options,
  debounceTime = 1000,
  maxResults = 200,
}: useFilterOptionsProps) => {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [lastFiltered, setLastFiltered] = useState<AutoCompleteOption[]>(options);
  const [lastQuery, setLastQuery] = useState("");

  useEffect(() => {
    if (query !== lastQuery) {
      setIsLoading(true);
      const handler = setTimeout(() => {
        setDebouncedQuery(query);
      }, debounceTime);

      return () => clearTimeout(handler);
    }
    return undefined;
  }, [query, debounceTime]);

  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    if (!debouncedQuery || debouncedQuery === "") return options.slice(0, maxResults);
    if (debouncedQuery === lastQuery) return lastFiltered.slice(0, maxResults);

    const source = debouncedQuery.startsWith(lastQuery) ? lastFiltered : options;

    const newFiltered = matchSorter(source, debouncedQuery, {
      keys: ["label"],
    });

    setLastFiltered(newFiltered);
    setLastQuery(debouncedQuery);
    setIsLoading(false);
    return newFiltered.slice(0, maxResults);
  }, [debouncedQuery, options]);

  return { query, setQuery, filteredOptions, isLoading };
};

export default useFilterOptions;
